/*
 *   Copyright (c) 2021 Propally.Inc
 *   All rights reserved.
 */
import "normalize.css";
import "./global.less";
import * as Sentry from "@sentry/browser";
import { CaptureConsole } from "@sentry/integrations";
import * as React from "react";
import * as ReactDOM from "react-dom";

import { AppErrorBoundary } from "@/components/AppErrorBoundary";
import { initializeAppInfo } from "@/utilities/appInfo";
import {
  initializeAppMeasurementTool,
  initializeATT,
  initializeOrientationLock,
  initializePushNotification,
  initializeStatusBarAppearance,
  isNativeApp,
} from "@/utilities/capacitorLogic";
import { registerChunkLoadErrorReload } from "@/utilities/chunkLoadErrorReload";
import { isReactErrorBoundaryConsoleEvent } from "@/utilities/reactErrorConsoleEvent";
import { scrubSentryBreadcrumb, scrubSentryEvent } from "@/utilities/sentryScrub";
import { isThirdPartyTagConsoleNoise } from "@/utilities/thirdPartyTagConsoleNoise";

import { PropallyApp } from "./app";

if (process.env.ENABLE_SENTRY) {
  Sentry.init({
    dsn: "https://ecf1dd4ff57d4faeb2274d0d74f78ef1@o561825.ingest.sentry.io/5802021",
    environment: process.env.SERVICE_ENVIRONMENT,
    release: process.env.GIT_COMMIT_HASH,
    integrations: [new CaptureConsole({ levels: ["error", "warn"] })],
    // ResizeObserver の通知スキップはブラウザ内部の再計測で起きる無害な現象で、
    // 残りの通知は次フレームで配信されるためアプリ側に打てる手が無い。onerror 経由の
    // 未処理エラーとして届くので、メッセージで除外する。ブラウザのバージョンで文言が
    // 2 種類あり、片方だけ除外すると別イシューとして再発するため両方を対象にする
    ignoreErrors: [
      "ResizeObserver loop completed with undelivered notifications",
      "ResizeObserver loop limit exceeded",
    ],
    // console のパンくずの引数（API エラーの本文・応答）と、画面遷移・通信の URL のトークンを伏せる
    beforeBreadcrumb: (breadcrumb) => scrubSentryBreadcrumb(breadcrumb),
    beforeSend(event, hint) {
      if (isReactErrorBoundaryConsoleEvent(event)) return null;
      const error = hint?.originalException;
      // axiosエラーかつ、401エラーで、URLがユーザ情報取得APIの場合は除外
      if (
        error &&
        typeof error === "object" &&
        "response" in error &&
        (error as any).response?.status === 401 &&
        (error as any).config?.url?.includes("/v1/session")
      ) {
        return null;
      }

      // Googleログイン関連の「ユーザーによるキャンセル」「Google側によるポップアップ閉じ」は除外
      if (
        error instanceof Error &&
        (
          error.message.includes("Cancelled by user") ||
          error.message.includes("Popup closed")
        )
      ) {
        return null;
      }

      // GTM 経由の第三者タグ（[oaiq] 等）の console ログは自社で直せないので除外。
      // scrubSentryEvent が extra.arguments のオブジェクトを置き換える前に判定する（順序の意図を残すため）
      if (isThirdPartyTagConsoleNoise(event)) {
        return null;
      }

      // console.error(error) 経由の axios エラーの本文・応答と、画面 URL のトークン（?prt= 等）を伏せる
      return scrubSentryEvent(event);
    },
  });
}

// 配信アセットの入れ替えに対する復旧はWebだけに登録する。Sentry無効時にも動作させる。
if (!isNativeApp) registerChunkLoadErrorReload();

if (isNativeApp) {
  initializeStatusBarAppearance();
  initializePushNotification();
  initializeAppMeasurementTool();
  initializeATT();
  initializeOrientationLock();
}

initializeAppInfo();

ReactDOM.render(
  <AppErrorBoundary>
    <PropallyApp />
  </AppErrorBoundary>,
  document.getElementById("propally-root")
);
